import * as React from "react";
import { FC, useCallback, useEffect, useMemo, useState } from "react";
import { connect } from "react-redux";
import { useParams } from "react-router";
import { bindActionCreators } from "redux";
import { InlineLoading } from "src/components/common";
import Tree, { ITree } from "src/components/common/Tree/Tree";
import { TrialInstitutionNotesBreadcrumbs } from "src/helpers/breadcrumbHelper";
import { TrialInstitutionSubMenu } from "src/helpers/subMenuHelper";
import useNavigate from "src/hooks/useNavigate";
import * as NavigationActions from "../../../actions/navigation";
import * as NoteTypesNotesActions from "../../../actions/noteTypeNotes";
import * as TrialInstitutionActions from "../../../actions/trialInstitution";
import { Link } from "../../../components/routing/index";
import {
    FontAwesomeIcon,
    FontAwesomeIcons,
} from "../../../constants/fontAwesomeIcons";
import * as Dtos from "../../../dtos/Tmd.dtos";
import { RequestState } from "../../../enumerations/RequestState";
import * as AuthenticationHelper from "../../../helpers/authentication";
import { ITmdState } from "../../../reducers/index";
import { TrialInstitutionLayout } from "../../index";

import "./TrialInstitutionNotesPage.scss";

interface TrialInstitutionNotesPageProps {
    trialInstitution: Dtos.TrialInstitution;
    loadingTrialInstitution: boolean;
    loadTrialInstitutionSuccess: boolean;
    loadTrialInstitutionFailure: boolean;
    loadTrialInstitutionErrorCode: string;
    noteTypeNotes: Dtos.NoteTypeNotes[];
    loadingNoteTypeNotes: boolean;
    loadNoteTypeNotesSuccess: boolean;
    loadNoteTypeNotesFailure: boolean;
    loadNoteTypeNotesErrorCode: string;
    permissions?: Dtos.PersonnelTmdPermissions[];
    clearTrialInstitution: TrialInstitutionActions.ITrialInstitutionClearActionCreator;
    loadTrialInstitution: TrialInstitutionActions.ITrialInstitutionLoadByIdActionCreator;
    clearNoteTypeNotes: NoteTypesNotesActions.INoteTypeNotesClearActionCreator;
    loadNoteTypeNotes: NoteTypesNotesActions.INoteTypeNotesLoadByTrialInstitutionIdActionCreator;
    setBreadcrumbs: NavigationActions.ISetBreadcrumbsActionCreator;
    setDrawerItems: NavigationActions.ISetDrawerItemsActionCreator;
}

const TrialInstitutionNotesPage: FC<TrialInstitutionNotesPageProps> = ({
    trialInstitution,
    loadingTrialInstitution,
    loadTrialInstitutionSuccess,
    loadTrialInstitutionFailure,
    loadTrialInstitutionErrorCode,
    noteTypeNotes,
    loadingNoteTypeNotes,
    loadNoteTypeNotesSuccess,
    loadNoteTypeNotesFailure,
    loadNoteTypeNotesErrorCode,
    permissions,
    clearTrialInstitution,
    loadTrialInstitution,
    clearNoteTypeNotes,
    loadNoteTypeNotes,
    setBreadcrumbs,
    setDrawerItems
}) => {
    //
    // -- Params --
    const { trialId } = useParams<{ trialId: string }>();
    const { trialInstitutionId } = useParams<{ trialInstitutionId: string }>();
    const trialIdNumber = parseInt(trialId, 10);
    const trialInstitutionIdNumber = parseInt(trialInstitutionId, 10);

    //
    // -- States --
    const [treeData, setTreeData] = useState<ITree | null>(null);
    const [initalizedTreeData, setInitalizedTreeData] =
        useState<boolean>(false);
    const [toggleState, setToggleState] = useState<string[]>([]);
    const [creatingTree, setCreatingTree] = useState<boolean>(false);

    //
    // -- Hooks --
    const navigate = useNavigate();

    //
    // -- Variables --
    const showTrialInsitution =
        !loadingTrialInstitution &&
        loadTrialInstitutionSuccess &&
        !loadingNoteTypeNotes &&
        loadNoteTypeNotesSuccess &&
        !creatingTree;


    // Load trialInstitution & notes
    useEffect(() => {
        if (trialIdNumber && trialInstitutionIdNumber && !trialInstitution && !loadingTrialInstitution) {
            setBreadcrumbs(
                TrialInstitutionNotesBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber
                )
            );

            setTreeData(null);

            loadTrialInstitution(trialInstitutionIdNumber);
            loadNoteTypeNotes(trialInstitutionIdNumber);
        }
    }, [
        trialIdNumber,
        trialInstitutionIdNumber,
        trialInstitution,
        loadingTrialInstitution,
        setBreadcrumbs,
        TrialInstitutionNotesBreadcrumbs,
        loadTrialInstitution,
        loadNoteTypeNotes,
    ]);

    // Navigate to error page on load failure
    useEffect(() => {
        if (loadTrialInstitutionFailure) {
            navigate(`/error/${loadTrialInstitutionErrorCode}`);
        }
        if (loadNoteTypeNotesFailure) {
            navigate(`/error/${loadNoteTypeNotesErrorCode}`);
        }
    }, [
        loadTrialInstitutionFailure,
        loadTrialInstitutionErrorCode,
        loadNoteTypeNotesFailure,
        loadNoteTypeNotesErrorCode,
        navigate,
    ]);

    // Update breadcrumbs / set drawer items
    useEffect(() => {
        if (trialInstitution) {
            setDrawerItems(
                TrialInstitutionSubMenu(trialInstitution, permissions)
            );
        }

        if (
            trialIdNumber &&
            trialInstitutionIdNumber &&
            trialInstitution?.trial?.displayName &&
            trialInstitution?.institution?.name
        ) {
            setBreadcrumbs(
                TrialInstitutionNotesBreadcrumbs(
                    trialIdNumber,
                    trialInstitutionIdNumber,
                    trialInstitution?.trial.displayName,
                    trialInstitution.institution.name
                )
            );
        }
    }, [
        trialInstitution,
        setDrawerItems,
        TrialInstitutionSubMenu,
        permissions,
        setBreadcrumbs,
        TrialInstitutionNotesBreadcrumbs,
        trialIdNumber,
        trialInstitutionIdNumber,
    ]);

    // Cleanup effect to clear the trial institution & notes on component unmount
    useEffect(() => {
        return () => {
            clearTrialInstitution();
            clearNoteTypeNotes();
        };
    }, [clearTrialInstitution, clearNoteTypeNotes]);

    // Update tree
    useEffect(() => {
        setInitalizedTreeData(false);
    }, [noteTypeNotes, setInitalizedTreeData]);

    // Create tree
    useEffect(() => {
        if (noteTypeNotes && !initalizedTreeData) {
            setCreatingTree(true);

            // If no trialInstitution notes return
            if (noteTypeNotes?.length == 0) {
                setCreatingTree(false);
                return;
            }

            const createChildNodes = (notes) => {
                let children = [];

                children =
                    notes
                        ?.sort((a, b) => a?.id - b?.id)
                        ?.map((y) => ({
                            id: `note-${y.id}`,
                            data: (
                                <div
                                    style={{
                                        display: "grid",
                                        gridTemplateColumns: " 1fr 3fr 8fr",
                                    }}
                                >
                                    <div className="px-1">
                                        <FontAwesomeIcon
                                            icon={FontAwesomeIcons.Duotone.NOTE}
                                        />
                                    </div>
                                    <div className="px-1">{y?.title}</div>
                                    <div className="d-inline-block px-1">
                                        <span
                                            className="text-truncate"
                                            style={{ whiteSpace: "pre-line" }}
                                        >
                                            {y?.comments}
                                        </span>
                                    </div>
                                </div>
                            ),
                            icon: null,
                            collapsible: false,
                        })) ?? [];

                // Add table header
                if (children?.length > 0) {
                    children.unshift({
                        id: "tableheader",
                        data: (
                            <div
                                style={{
                                    display: "grid",
                                    gridTemplateColumns: "1fr 3fr 8fr",
                                }}
                            >
                                <div className="px-1"></div>
                                <div className="px-1">
                                    <strong>Title</strong>
                                </div>
                                <div className="px-1">
                                    <strong>Comment</strong>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: false,
                    });
                }

                return children;
            };

            let treeNodes =
                noteTypeNotes
                    ?.sort((a, b) =>
                        a?.noteType?.value.localeCompare(b?.noteType?.value)
                    )
                    ?.map((x) => ({
                        id: `type-${x.noteTypeId}`,
                        data: (
                            <div className="d-flex align-items-center">
                                <FontAwesomeIcon
                                    icon={FontAwesomeIcons.Duotone.NOTESMEDICAL}
                                />
                                <span
                                    className="pl-4 mb-0"
                                    style={{ width: "180px" }}
                                >
                                    {x.noteType?.value}
                                </span>
                                <div
                                    className="d-flex align-items-center justify-content-center"
                                    style={{ width: "50px" }}
                                >
                                    <span className="badge badge-pill badge-secondary mb-0">
                                        {x?.notes?.length}
                                    </span>
                                </div>
                            </div>
                        ),
                        icon: null,
                        collapsible: true,
                        subTree: {
                            children: [...createChildNodes(x.notes)],
                        },
                    })) ?? [];

            // Initialize tree toggle
            const expanded = treeNodes.map((z) => z.id);

            setToggleState(expanded);
            setTreeData({ children: treeNodes });
            setInitalizedTreeData(true);
            setCreatingTree(false);
        }
    }, [
        noteTypeNotes,
        initalizedTreeData,
        treeData,
        setTreeData,
        setToggleState,
        setInitalizedTreeData,
        setCreatingTree,
    ]);

    const handleToggle = useCallback(
        (id: string, navigateFlag?: boolean) => {
            if (navigateFlag) {
                if (
                    id.includes("note") &&
                    AuthenticationHelper.hasPermission(
                        permissions,
                        Dtos.Permission.InstitutionNoteView
                    )
                ) {
                    navigate(
                        `/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/notes/${id.replace(
                            "note-",
                            ""
                        )}/details`
                    );
                }
                if (id.includes("type")) {
                    setToggleState((prev) =>
                        prev.includes(id)
                            ? prev.filter((nodeId) => nodeId !== id)
                            : [...prev, id]
                    );
                }
            } else {
                setToggleState((prev) =>
                    prev.includes(id)
                        ? prev.filter((nodeId) => nodeId !== id)
                        : [...prev, id]
                );
            }
        },
        [
            setToggleState,
            navigate,
            trialIdNumber,
            trialInstitutionIdNumber,
            permissions,
        ]
    );

    const renderContent = useMemo(() => {
        return (
            <div>
                <div className="row">
                    <div className={"col-9 mb-2"}>
                        <h1>Notes</h1>
                    </div>
                    <div className="col-3" style={{ textAlign: "right" }}>
                        {trialInstitution &&
                            trialInstitution.trialId > 0 &&
                            AuthenticationHelper.hasPermission(
                                permissions,
                                Dtos.Permission.TrialNoteCreate,
                                trialInstitution.trialId,
                                trialInstitution.institutionId
                            ) && (
                                <Link
                                    className={"btn btn-primary"}
                                    url={`/trials/${trialIdNumber}/trialinstitution/${trialInstitutionIdNumber}/notes/create`}
                                >
                                    Create
                                </Link>
                            )}
                    </div>
                </div>
                {!showTrialInsitution ? (
                    <InlineLoading absoluteCenter={true} size={50} />
                ) : null}
                {showTrialInsitution ? (
                    <>
                        {treeData && treeData?.children?.length > 0 ? (
                            <Tree
                                treeData={treeData}
                                onToggle={handleToggle}
                                toggleState={toggleState}
                            />
                        ) : (
                            <div className="card card-shadow p-3">
                                <div className="alert alert-info d-flex mb-0">
                                    <div className="mr-2">
                                        <FontAwesomeIcon
                                            icon={
                                                FontAwesomeIcons.Solid
                                                    .INFO_SQUARE
                                            }
                                            fixedWidth
                                        />
                                    </div>
                                    <span>
                                        There are currently no trial institution
                                        notes
                                    </span>
                                </div>
                            </div>
                        )}
                    </>
                ) : null}
            </div>
        );
    }, [
        trialInstitution,
        trialIdNumber,
        trialInstitutionIdNumber,
        showTrialInsitution,
        treeData,
        handleToggle,
        toggleState,
    ]);

    return (
        <TrialInstitutionLayout
            loading={loadingTrialInstitution || loadingNoteTypeNotes}
            permission={Dtos.Permission.TrialNoteView}
        >
            {renderContent}
        </TrialInstitutionLayout>
    );
};

const mapStateToProps = (
    state: ITmdState
): Partial<TrialInstitutionNotesPageProps> => {
    let trialInstitution: Dtos.TrialInstitution | undefined = !(
        state.trialInstitution.formData instanceof Array
    )
        ? state.trialInstitution.formData
        : undefined;
    let noteTypeNotes: Dtos.NoteTypeNotes[] | undefined = (state.noteTypeNotes.formData instanceof Array)
            ? state.noteTypeNotes.formData
            : undefined;
    let permissions = state.user.userSummary
        ? state.user.userSummary.permissions
        : [];

    return {
        trialInstitution: trialInstitution!,
        loadingTrialInstitution:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Pending,
        loadTrialInstitutionSuccess:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Success,
        loadTrialInstitutionFailure:
            state.trialInstitution.loadState &&
            state.trialInstitution.loadState.status == RequestState.Failure,
        loadTrialInstitutionErrorCode:
            state.trialInstitution.loadState.errorCode,
        noteTypeNotes: noteTypeNotes!,
        loadingNoteTypeNotes:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Pending,
        loadNoteTypeNotesSuccess:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Success,
        loadNoteTypeNotesFailure:
            state.noteTypeNotes.loadState &&
            state.noteTypeNotes.loadState.status == RequestState.Failure,
        loadNoteTypeNotesErrorCode: state.noteTypeNotes.loadState.errorCode,
        permissions: permissions,
    };
};

const mapDispatchToProps = (
    dispatch
): Partial<TrialInstitutionNotesPageProps> => {
    return {
        loadTrialInstitution: bindActionCreators(
            TrialInstitutionActions.LoadTrialInstitutionById,
            dispatch
        ),
        clearTrialInstitution: bindActionCreators(
            TrialInstitutionActions.Clear,
            dispatch
        ),
        loadNoteTypeNotes: bindActionCreators(NoteTypesNotesActions.LoadNoteTypeNotesByTrialInstitutionId, dispatch),
        clearNoteTypeNotes: bindActionCreators(NoteTypesNotesActions.Clear, dispatch),
        setBreadcrumbs: bindActionCreators(
            NavigationActions.SetBreadcrumbs,
            dispatch
        ),
        setDrawerItems: bindActionCreators(
            NavigationActions.SetDrawerItems,
            dispatch
        )
    };
};

export default connect(
    mapStateToProps,
    mapDispatchToProps
)(TrialInstitutionNotesPage);
